import { Inject } from '@odinjs/odin';
import { BasicFile, FieldAPI, FileUploaderAPI, FormAPI, MemoryFileHandler, PredefinedFile, ToastNotificationAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { FragmentNames, PseudonymAppointmentsStepDocumentUploadAliases } from '../../codegen';
import PseudonymAppointmentsBaseController from './pseudonym-appointments-base-controller';
import { ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { DocumentUploadScheduleRuleResponse, PseudonymCustomMenssage } from './pseudonym-interfaces';
import { AppointmentsSteps, CUSTOM_MESSAGE_ACTION } from './pseudonym-types';
import PseudonymAppointmentModel from './pseudonym-appointments-model';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { FileProviderData } from '../../constants/file-provider-enum';
import { YES } from '../pseudonym-appointments/pseudonym-types';

@Controller({ component: PseudonymAppointmentsStepDocumentUploadAliases.LAYOUT_UPLOAD_DOCUMENTS, fragment: FragmentNames.PSEUDONYM_APPOINTMENTS_STEP_DOCUMENT_UPLOAD })
export default class PseudonymAppointmentsStepDocumentsController extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymAppointmentsStepDocumentUploadAliases.FORM_UPLOADER_DOCUMENTS)
    fileUploaderDocumentsFormAPI!: FormAPI;

  @Inject(PseudonymAppointmentsStepDocumentUploadAliases.FILE_UPLOADER_DOCUMENTS)
    fileUploaderDocumentsAPI!: FileUploaderAPI;

  @Inject(PseudonymAppointmentsStepDocumentUploadAliases.FIELD_UPLOADER_DOCUMENTS)
    fieldUploaderDocumentsAPI!: FieldAPI;

  @Inject(PseudonymAppointmentsStepDocumentUploadAliases.TOAST_NOTIFICATION_UPLOAD_DOCUMENT)
    notificationUploaderDocumentsAPI!: ToastNotificationAPI;

  @OnMount(PseudonymAppointmentsStepDocumentUploadAliases.LAYOUT_UPLOAD_DOCUMENTS)
  onMountFileUploaderLayout(): void {
    PseudonymAppointmentModel.stepDocumentsUpload.populateFileUploaderItems = () => this.populateFileUploaderItems();
  }

  @OnMount(PseudonymAppointmentsStepDocumentUploadAliases.FILE_UPLOADER_DOCUMENTS)
  onMountFileUploader(event: MountEvent): void {
    const fileUploaderAPI = event.target as FileUploaderAPI;
    fileUploaderAPI.setFileHandlerProviderName(FileProviderData.fileProviderMemory);
  }

  @OnChange(PseudonymAppointmentsStepDocumentUploadAliases.FILE_UPLOADER_DOCUMENTS)
  async onChangeFileUploader(event: ChangeEvent<BasicFile[]>): Promise<void> {
    PseudonymAppointmentModel.stepDocumentsUpload.uploadedFiles = event.value;
    const memoryFileHandler = this.fileUploaderDocumentsAPI.getFileHandlerInstance() as MemoryFileHandler;
    const memoryFiles = memoryFileHandler.getMemoryFiles();
    PseudonymAppointmentModel.stepDocumentsUpload.memoryFiles = memoryFiles;
    this.setWizardStepDone(AppointmentsSteps.STEP_DOCUMENTS, await this.uploadDocumentsIsValid());
  }

  async uploadDocumentsIsValid(): Promise<boolean> {
    const requiredFiles = this.fileUploaderDocumentsAPI.getPredefinedFiles()?.filter(e => e.required) || [];
    if (requiredFiles.length === 0) {
      return true;
    }

    const uploadedFiles = PseudonymAppointmentModel.stepDocumentsUpload.uploadedFiles || [];
    if (uploadedFiles.length === 0) {
      return false;
    }

    const isValid = requiredFiles.every(itemRule =>
      uploadedFiles.some(file => file.predefinedFileKey === itemRule.key)
    );

    await this.fieldUploaderDocumentsAPI.validate();
    return isValid;
  }

  async populateFileUploaderItems(): Promise<void> {
    this.fileUploaderDocumentsFormAPI.setLoading(true);

    if (await this.verifyThereIsUploadDocuments()) {
      this.fileUploaderDocumentsFormAPI.setLoading(false);
    }

    const predefinedFiles = this.getPredefinedFile(PseudonymAppointmentModel.stepDocumentsUpload.documentsToUpload);
    this.fileUploaderDocumentsAPI.setPredefinedFiles(predefinedFiles);

    const customMessageResponse = await HttpService.get(APPLICATION_ENDPOINTS.GET_WSUITE_CUSTOM_MESSAGE, { action: CUSTOM_MESSAGE_ACTION.UPLOAD_SCHEDULING_ACTION });
    const customMessage = customMessageResponse.body as PseudonymCustomMenssage;
    const expression = customMessage?.message ?? MessageService.translate('schedule.default.upload.document.message');
    this.notificationUploaderDocumentsAPI.setMessage(expression);
    this.setWizardStepDone(AppointmentsSteps.STEP_DOCUMENTS, await this.uploadDocumentsIsValid());
    this.fileUploaderDocumentsFormAPI.setLoading(false);
  }

  async verifyThereIsUploadDocuments(): Promise<boolean> {
    const insurance = PseudonymAppointmentModel.stepOne.healthInsurance.healthInsuranceId;
    const internalProcedures = PseudonymAppointmentModel.stepTwo.procedures.filter(procedure => procedure.internalProcedureSequence)?.map(procedure => procedure.internalProcedureSequence)
    const procedureType = Array.from(new Set(
      PseudonymAppointmentModel.stepTwo.procedures.map(procedure => procedure.typeProcedureCode)
    ));
    const uploadRulesResponse = await HttpService.get(APPLICATION_ENDPOINTS.APPOINTMENT_DOCUMENT_UPLOAD_RULE, {
      insurance,
      internalProcedures,
      procedureType
    });

    const documentUploadRule = uploadRulesResponse.body as DocumentUploadScheduleRuleResponse[];
    PseudonymAppointmentModel.stepDocumentsUpload.hasFilesToUpload = documentUploadRule.length > 0;

    if (!PseudonymAppointmentModel.stepDocumentsUpload.hasFilesToUpload) {
      return false;
    }

    PseudonymAppointmentModel.stepDocumentsUpload.documentsToUpload = documentUploadRule;
    return true;
  }

  private getPredefinedFile(documentUploadRule: DocumentUploadScheduleRuleResponse[]): PredefinedFile[] {
    const predefinedFiles = [] as PredefinedFile[];
    documentUploadRule.forEach(element => {
      const file = {
        key: String(element.sequence),
        required: element.required === YES,
        value: element.description
      } as unknown as PredefinedFile;

      predefinedFiles.push(file);
    });

    return predefinedFiles;
  }
}
